import React from 'react';
import { motion } from 'framer-motion';
import { Truck, Package, Factory, ShoppingCart, HardHat, Coffee, Sprout, ArrowUpRight } from 'lucide-react';

const services = [
  { title: 'Full Truck Load',        icon: Truck,         desc: 'Dedicated trucks exclusively for your large-scale shipments. Ideal for bulk cargo, wholesale goods, and interstate commercial transport across India.',                                                    tag: 'FTL' },
  { title: 'Part Load (LTL)',        icon: Package,       desc: 'Cost-effective shared logistics for smaller consignments. Pay only for the space you use with the same premium handling and tracking.',                                                                    tag: 'LTL' },
  { title: 'Industrial Logistics',   icon: Factory,       desc: 'Expert handling of heavy machinery, industrial equipment, and manufacturing components with specialized loading and securing protocols.',                                                                   tag: 'Industrial' },
  { title: 'E-Commerce Delivery',    icon: ShoppingCart,  desc: 'Fast and reliable middle-mile and last-mile delivery services built for e-commerce SLAs. Integrated tracking and proof of delivery.',                                                                     tag: 'E-Com' },
  { title: 'Construction Materials', icon: HardHat,       desc: 'Specialized transport for bulk construction materials — cement, steel, bricks, and infrastructure supplies delivered to project sites on time.',                                                           tag: 'Construction' },
  { title: 'FMCG Distribution',      icon: Coffee,        desc: 'End-to-end supply chain management for fast-moving consumer goods. Temperature-aware handling and time-critical delivery windows.',                                                                        tag: 'FMCG' },
  { title: 'Agriculture Products',   icon: Sprout,        desc: 'Farm-to-market logistics for perishable produce, bulk agri commodities, and seasonal harvests with priority dispatch and cold-chain options.',                                                             tag: 'Agri' },
];

const cardVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  visible: (i) => ({
    opacity: 1, y: 0, scale: 1,
    transition: { delay: i * 0.08, type: 'spring', stiffness: 90, damping: 15 },
  }),
};

const Services = () => (
  <section id="services" className="section-padding bg-brand-bg relative overflow-hidden">
    <motion.div
      animate={{ x: [0, 40, 0], y: [0, -30, 0] }}
      transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
      className="absolute top-0 right-0 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] bg-brand-orange/6 blur-[150px] rounded-full pointer-events-none"
    />
    <motion.div
      animate={{ x: [0, -30, 0], y: [0, 40, 0] }}
      transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
      className="absolute bottom-0 left-0 w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bg-brand-orange/4 blur-[120px] rounded-full pointer-events-none"
    />

    <div className="max-w-7xl mx-auto relative z-10">
      <div className="mb-10 sm:mb-16">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="flex items-center gap-3 mb-4"
        >
          <motion.div
            animate={{ scaleX: [0, 1] }}
            transition={{ duration: 0.8 }}
            className="h-[2px] w-12 bg-brand-orange"
          />
          <span className="text-brand-orange font-black tracking-[0.3em] sm:tracking-[0.4em] text-[10px] sm:text-xs uppercase">What We Do</span>
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-brand-black tracking-tighter mb-4"
        >
          PREMIUM LOGISTICS<br className="hidden sm:block" /> SOLUTIONS
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="text-brand-muted max-w-2xl text-sm sm:text-base leading-relaxed"
        >
          From small parcel deliveries to heavy industrial freight — PKROUTEX provides end-to-end logistics solutions tailored to every business need across India.
        </motion.p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
        {services.map((service, idx) => (
          <motion.div
            key={idx}
            custom={idx}
            variants={cardVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            whileHover={{ y: -10, scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="relative bg-white p-5 sm:p-6 md:p-7 rounded-2xl group transition-all duration-500 border border-black/5 shadow-sm hover:border-brand-orange/30 hover:shadow-xl cursor-pointer overflow-hidden"
          >
            <div className="absolute top-0 -left-full w-2/3 h-full bg-gradient-to-r from-transparent via-brand-orange/5 to-transparent skew-x-[-20deg] group-hover:left-[150%] transition-all duration-700 ease-in-out pointer-events-none" />

            <span className="absolute top-4 sm:top-5 right-4 sm:right-5 text-[9px] font-black tracking-widest uppercase text-brand-orange/60 bg-brand-orange/8 px-2 py-1 rounded-full border border-brand-orange/15">
              {service.tag}
            </span>

            <motion.div
              whileHover={{ rotate: 10, scale: 1.15 }}
              className="w-12 sm:w-14 h-12 sm:h-14 bg-brand-orange/10 rounded-xl flex items-center justify-center mb-4 sm:mb-5 group-hover:bg-brand-orange transition-colors duration-500"
            >
              <service.icon className="text-brand-orange group-hover:text-white transition-colors duration-500" size={24} />
            </motion.div>

            <h3 className="text-lg sm:text-xl font-black mb-2 sm:mb-3 text-brand-black group-hover:text-brand-orange transition-colors duration-300">
              {service.title}
            </h3>
            <p className="text-brand-muted text-sm leading-relaxed group-hover:text-brand-black transition-colors">
              {service.desc}
            </p>

            <motion.div
              initial={{ opacity: 0, x: -5 }}
              whileHover={{ opacity: 1, x: 0 }}
              className="mt-4 flex items-center gap-1 text-brand-orange text-xs font-black uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-all duration-300"
            >
              Learn More <ArrowUpRight size={14} />
            </motion.div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export default Services;
